import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductAddToCartButtonCustom from "@theStellarBlueprint/Components/ProductAddToCartButtonCustom";
import ProductPartNumbersCustom, {
    ProductPartNumbersStyles,
} from "@theStellarBlueprint/Components/ProductPartNumbersCustom";
import ProductPriceCustom from "@theStellarBlueprint/Components/ProductPriceCustom";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    currentUserIsGuest: getCurrentUserIsGuest(state),
    userName: state.context.session.userName,
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductCustomStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductCustomStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    priceBatch?: InjectableCss;
    priceContent?: InjectableCss;
}

export const productCarouselProductCustomStyles: ProductCarouselProductCustomStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                padding: 0 15px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: center;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            `,
            typographyProps: {
                css: css`
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    color: #06274c !important;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 0px;
                    font-weight: 500;
                `,
            },
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 1;
                margin-bottom: 6px;
                padding: 0 15px;
            `,
        },
        erpNumberGridItem: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: center;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            `,
        },
        erpNumberLabelText: {
            size: 13,
            css: css`
                display: none;
            `,
        },
        erpNumberValueText: {
            size: 13,
            css: css`
                text-transform: uppercase;
                color: #0000ff;
                font-size: 14px;
                font-weight: 500;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 0px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 12 },
    productAddToCartButtonStyles: {
        icon: { src: ShoppingCart, position: "left" },
        typographyProps: {
            css: css`
                font-weight: 500;
                font-size: 13px;
            `,
        },
        css: css`
            width: 100%;
            & svg {
                color: #ffffff;
                stroke-width: 3;
            }
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    productImageStyles: {
        image: {
            css: css`
                & p {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 4;
                    -webkit-box-orient: vertical;
                    height: 146px;
                    padding: 18px;
                }
                margin-bottom: 10px;
            `,
        },
        linkWrappingImage: {
            css: css`
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: center;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            `,
        },
    },
    priceBatch: {
        css: css`
            height: 35px;
            width: 125px;
            background-image: url("/userfiles/images/icons/PriceBatch.svg");
            background-repeat: no-repeat;
            position: absolute;
            z-index: 99;
            display: inline-block;
            top: 5px;
        `,
    },
    priceContent: {
        css: css`
            color: white;
            text-decoration: none;
            margin-left: 10px;
            font-size: 11px;
            font-weight: 700;
            position: absolute;
            top: 5px;
        `,
    },
};

const ProductCarouselProductCustom: React.FC<Props> = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    currentUserIsGuest,
    userName,
}) => {
    const [styles] = React.useState(() => mergeToNew(productCarouselProductCustomStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <>
            <ProductContext.Provider value={productContext}>
                {showImage && <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />}
                {showBrand && product.brand && (
                    <StyledWrapper {...styles.productBrandWrapper}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}
                {showTitle && (
                    <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                )}
                {showPartNumbers && (
                    <ProductPartNumbersCustom
                        productNumber={product.productNumber}
                        customerProductNumber=""
                        manufacturerItem=""
                        showCustomerName={false}
                        showManufacturerItem={false}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                )}
                {/* {showPrice && (
                    <StyledWrapper {...styles.productPriceWrapper}>
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice showLabel={false} product={productContext} />
                        {product.unitOfMeasures && (
                            <ProductUnitOfMeasureSelect
                                labelOverride=""
                                extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                id={`${carouselId}-uom`}
                            />
                        )}
                        <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                    </StyledWrapper>
                )} */}
                {showAddToCart && (
                    <GridContainer {...styles.addToCartContainer}>
                        {/* <GridItem {...styles.qtyGridItem}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY_quantity")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${carouselId}-qty`}
                            />
                        </GridItem> */}
                        <GridItem {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButtonCustom
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </GridItem>
                    </GridContainer>
                )}
                {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )}

                <StyledWrapper {...styles.priceBatch}>
                    <StyledWrapper {...styles.priceContent}>
                        {userName && !currentUserIsGuest && (
                            <ProductPriceCustom
                                showLabel={false}
                                showSavings={false}
                                showSavingsAmount={false}
                                showSavingsPercent={false}
                                product={productContext}
                            />
                        )}
                        {(!userName || (userName && currentUserIsGuest)) && translate("Login For Price")}
                    </StyledWrapper>
                </StyledWrapper>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductCustom);
